/**
 * Property CMS JSON is baked into `generated/*.json` at build time. After an admin publishes,
 * compare GET /v1/property/version with `version.propertyFingerprint` from the bundle; when the
 * server tree changed, fetch GET /v1/property/:locale and apply via `onUpdate`.
 */

import { CONFIG } from '../config';
import { shouldSyncPublicCmsFromApi } from './publicCmsSource.js';

const SHA256_HEX = /^[a-f0-9]{64}$/i;

let cachedContentVersions = null;
let inFlightContentVersionsPromise = null;
const lastSyncedServerContentFingerprint = {};
const inFlightContentSyncByLocale = {};
const inFlightContentBodyByLocale = {};

function shouldUseRemoteContentCheck(bundledFingerprint) {
  return SHA256_HEX.test(bundledFingerprint || '');
}

export function bumpPublishedContentSync() {
  for (const k of Object.keys(lastSyncedServerContentFingerprint)) {
    delete lastSyncedServerContentFingerprint[k];
  }
  cachedContentVersions = null;
  inFlightContentVersionsPromise = null;
}

export function resetPublishedContentSyncStateForTests() {
  bumpPublishedContentSync();
  for (const k of Object.keys(inFlightContentSyncByLocale)) {
    delete inFlightContentSyncByLocale[k];
  }
  for (const k of Object.keys(inFlightContentBodyByLocale)) {
    delete inFlightContentBodyByLocale[k];
  }
}

async function fetchContentVersions() {
  if (cachedContentVersions) {
    return cachedContentVersions;
  }
  if (inFlightContentVersionsPromise) {
    return inFlightContentVersionsPromise;
  }
  const p = (async () => {
    try {
      const res = await fetch(`${CONFIG.API_BASE_URL}/property/version`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!data.success || !data.versions || typeof data.versions !== 'object') {
        throw new Error('Invalid /property/version payload');
      }
      cachedContentVersions = data.versions;
      return cachedContentVersions;
    } finally {
      if (inFlightContentVersionsPromise === p) {
        inFlightContentVersionsPromise = null;
      }
    }
  })();
  inFlightContentVersionsPromise = p;
  return p;
}

async function fetchContentBody(locale) {
  if (inFlightContentBodyByLocale[locale]) {
    return inFlightContentBodyByLocale[locale];
  }
  const p = (async () => {
    const res = await fetch(`${CONFIG.API_BASE_URL}/property/${locale}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    // Fresh environments may not have published property content yet; treat that as a valid empty payload.
    if (!data.success) {
      throw new Error(data.error || 'No property content for this language');
    }
    return data;
  })();
  inFlightContentBodyByLocale[locale] = p;
  try {
    return await p;
  } finally {
    if (inFlightContentBodyByLocale[locale] === p) {
      delete inFlightContentBodyByLocale[locale];
    }
  }
}

async function runContentSync({ locale, bundledFingerprint, bundledContent, onUpdate }) {
  if (typeof window === 'undefined') return;
  if (!shouldSyncPublicCmsFromApi()) return;
  if (!shouldUseRemoteContentCheck(bundledFingerprint)) return;
  if (typeof onUpdate !== 'function') return;

  const versions = await fetchContentVersions();
  const serverFp = versions[locale];
  if (!serverFp || typeof serverFp !== 'string') return;

  if (serverFp === bundledFingerprint) {
    const baseline =
      bundledContent != null && typeof bundledContent === 'object'
        ? JSON.parse(JSON.stringify(bundledContent))
        : {};
    onUpdate(locale, baseline);
    lastSyncedServerContentFingerprint[locale] = serverFp;
    return;
  }

  if (serverFp === lastSyncedServerContentFingerprint[locale]) {
    return;
  }

  const body = await fetchContentBody(locale);
  onUpdate(locale, body);
  lastSyncedServerContentFingerprint[locale] = serverFp;
}

export async function syncPublishedPropertyContentIfNeeded({
  locale,
  bundledFingerprint,
  bundledContent,
  onUpdate
}) {
  if (typeof window === 'undefined') return;
  if (!shouldSyncPublicCmsFromApi()) return;
  if (!shouldUseRemoteContentCheck(bundledFingerprint)) return;
  if (typeof onUpdate !== 'function') return;

  if (inFlightContentSyncByLocale[locale]) {
    return inFlightContentSyncByLocale[locale];
  }

  const syncPromise = (async () => {
    try {
      await runContentSync({ locale, bundledFingerprint, bundledContent, onUpdate });
    } catch (e) {
      console.warn(`property remote sync failed (${locale}):`, e.message);
    }
  })();

  inFlightContentSyncByLocale[locale] = syncPromise;
  try {
    await syncPromise;
  } finally {
    if (inFlightContentSyncByLocale[locale] === syncPromise) {
      delete inFlightContentSyncByLocale[locale];
    }
  }
}
